'use client';

/**
 * First-party traffic tracker provider. Mounted in both root layouts next to
 * <GoogleAnalytics /> (dual-run phase — neither knows about the other).
 *
 * Responsibilities:
 *   - initTracker() lifecycle hooks (flush on hide, consent transitions)
 *   - pageview on initial load (nav:'hard') and on every App Router
 *     soft-navigation (nav:'soft'), via usePathname
 *   - one-shot ai_referral event per session when an AI source is detected
 *     (separate sessionStorage guard from the GA4 one — both pipelines fire)
 *   - delegated click tracking: ONE document-level listener reads
 *     data-track-event / data-track-props attributes, so server components
 *     (e.g. view-more-link) get tracked without becoming client components.
 *
 * Renders nothing.
 */

import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { detectAiSource } from '@/lib/analytics';
import { initTracker, track, trackPageview } from '@/lib/tracking/tracker';

const AI_REFERRAL_FIRED_KEY = 'inceptionai:traffic_ai_referral_fired';

function fireAiReferralOnce(): void {
  try {
    if (sessionStorage.getItem(AI_REFERRAL_FIRED_KEY) === '1') return;
    const aiSource = detectAiSource();
    if (!aiSource) return;
    track('ai_referral', { ai_source: aiSource, landing: window.location.pathname });
    sessionStorage.setItem(AI_REFERRAL_FIRED_KEY, '1');
  } catch {
    // tracking never throws
  }
}

let pageReloadedFired = false;

function firePageReloadedDiagnostic(): void {
  if (pageReloadedFired) return;
  pageReloadedFired = true;
  try {
    const nav = performance.getEntriesByType('navigation')[0] as
      | PerformanceNavigationTiming
      | undefined;
    // wasDiscarded (Chrome): the tab was killed and this load restored it —
    // the exact "screen suddenly back at the top" a memory-pressured phone
    // produces. navigation type 'reload' also covers iOS Safari's silent
    // "this webpage was reloaded" kills (indistinguishable from a manual
    // refresh at the API level — the skew in the rollup tells them apart).
    const wasDiscarded = (document as Document & { wasDiscarded?: boolean }).wasDiscarded === true;
    if (!wasDiscarded && nav?.type !== 'reload') return;
    const dark = document.documentElement.classList.contains('dark');
    // eslint-disable-next-line no-console
    console.warn('[traffic] this page load was a reload', { wasDiscarded, navType: nav?.type, dark });
    track('page_reloaded', {
      was_discarded: wasDiscarded,
      nav_type: nav?.type ?? 'unknown',
      dark,
      viewport_w: window.innerWidth,
    });
  } catch {
    // tracking never throws
  }
}

function handleDelegatedClick(e: MouseEvent): void {
  try {
    const target = (e.target as Element | null)?.closest?.('[data-track-event]');
    if (!target) return;
    const eventType = target.getAttribute('data-track-event');
    if (!eventType) return;
    let props: Record<string, unknown> | undefined;
    const rawProps = target.getAttribute('data-track-props');
    if (rawProps) {
      try {
        props = JSON.parse(rawProps);
      } catch {
        props = undefined;
      }
    }
    // Only taxonomy client events pass schema validation server-side; anything
    // else is dropped by /api/t — no client-side allowlist needed.
    track(eventType as Parameters<typeof track>[0], props);
  } catch {
    // tracking never throws
  }
}

export function TrafficTracker() {
  const pathname = usePathname();
  const previousPath = useRef<string | null>(null);

  useEffect(() => {
    initTracker();
    fireAiReferralOnce();
    firePageReloadedDiagnostic();
    document.addEventListener('click', handleDelegatedClick, { capture: true });
    return () => document.removeEventListener('click', handleDelegatedClick, { capture: true });
  }, []);

  useEffect(() => {
    if (!pathname) return;
    if (previousPath.current === null) {
      trackPageview('hard');
    } else if (previousPath.current !== pathname) {
      trackPageview('soft', previousPath.current);
    }
    previousPath.current = pathname;
  }, [pathname]);

  return null;
}
